import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../api';

const DEFAULT_REGION = 'eu'; // projene göre: "eu" | "us" | "tr" | "global"

export default function ConsentPage() {
  const { t } = useTranslation();
  const { presenceId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, formRes] = await Promise.all([
        api.get(`/consent/status/${presenceId}`),
        api.get(`/consent/latest/${DEFAULT_REGION}`),
      ]);
      setStatus(statusRes.data);
      setForm(formRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [presenceId]);

  const handleAccept = async () => {
    if (!form) return;
    setAccepting(true);
    try {
      await api.post('/consent/accept', {
        presenceId: presenceId,
        region: DEFAULT_REGION,
        formId: form.id,
        meta: {},
      });
      
      // Success: Show toast and redirect to chat
      toast.success(t('presence.consentAccepted'), {
        description: t('presence.consentAcceptedDesc')
      });
      
      // Small delay for toast to show, then redirect
      setTimeout(() => {
        navigate(`/presence/${presenceId}/chat`);
      }, 800);
      
    } catch (err) {
      console.error(err);
      toast.error(t('presence.consentAcceptError'), {
        description: err.response?.data?.detail || t('presence.tryAgain')
      });
      setAccepting(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await api.post('/consent/withdraw', {
        presenceId: presenceId,
        reason: 'User-requested via UI',
        details: {},
      });
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-cyan-400/80 mb-1">
          {t('presence.consentLegalBasis')}
        </div>
        <p className="text-sm text-slate-300">
          {t('presence.consentDescription')}
        </p>
      </header>

      {loading && <div className="text-sm text-slate-400">{t('presence.loadingConsent')}</div>}

      {!loading && (
        <>
          {/* Status */}
          <section className="border border-slate-800 rounded-2xl bg-slate-900/70 px-4 py-3 text-sm">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xs text-slate-400">{t('presence.currentStatus')}</div>
                <div className="text-base font-medium text-slate-100">
                  {status?.status === 'active'
                    ? t('presence.activeConsent')
                    : status?.status === 'withdrawn'
                    ? t('presence.withdrawn')
                    : t('presence.noConsent')}
                </div>
                {status?.accepted_at && (
                  <div className="text-xs text-slate-500">
                    {t('presence.acceptedAt')}: {new Date(status.accepted_at).toLocaleString()}
                  </div>
                )}
                {status?.withdrawn_at && (
                  <div className="text-xs text-slate-500">
                    {t('presence.withdrawnAt')}: {new Date(status.withdrawn_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {status?.status !== 'active' && (
                  <button
                    onClick={handleAccept}
                    disabled={accepting || !form}
                    className="px-3 py-1.5 text-xs rounded-full bg-emerald-400 text-slate-900 font-semibold hover:bg-emerald-300 disabled:opacity-60"
                  >
                    {accepting ? t('presence.accepting') : t('presence.acceptConsent')}
                  </button>
                )}
                {status?.status === 'active' && (
                  <button
                    onClick={handleWithdraw}
                    disabled={withdrawing}
                    className="px-3 py-1.5 text-xs rounded-full border border-rose-500 text-rose-300 hover:bg-rose-500/10 disabled:opacity-60"
                  >
                    {withdrawing ? t('presence.withdrawing') : t('presence.withdrawConsent')}
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Latest form */}
          {form && (
            <section className="border border-slate-800 rounded-2xl bg-slate-950/80 px-4 py-4 text-sm">
              <div className="mb-2 flex justify-between items-center">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-[0.25em]">
                    {t('presence.latestForm')} ({form.region})
                  </div>
                  <div className="text-base font-medium text-slate-100">
                    {form.title} – {form.version}
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  {t('presence.effectiveFrom')} {new Date(form.effective_from).toLocaleDateString()}
                </div>
              </div>
              <div className="mt-2 prose prose-invert prose-sm max-w-none">
                {/* Extract language-specific section from markdown */}
                <pre className="whitespace-pre-wrap text-slate-200 bg-slate-900/60 rounded-lg px-3 py-2 border border-slate-800">
                  {(() => {
                    const currentLang = t('lang'); // 'tr' or 'en'
                    const content = form.body_md || '';
                    
                    // If content has both languages separated by "---" or language markers
                    // Try to extract only the relevant language section
                    
                    // Simple approach: split by common markers and show relevant part
                    if (currentLang === 'tr') {
                      // Extract Turkish section (before "---" or before "**English:**")
                      const sections = content.split(/\n---\n|\*\*English:\*\*/);
                      if (sections.length > 1) {
                        return sections[0].trim();
                      }
                    } else {
                      // Extract English section (after "---" or after "**English:**")
                      const englishMatch = content.match(/\*\*English:\*\*([\s\S]*)/);
                      if (englishMatch) {
                        return englishMatch[1].trim();
                      }
                      
                      // Or content after "---"
                      const sections = content.split(/\n---\n/);
                      if (sections.length > 1) {
                        return sections[1].trim();
                      }
                    }
                    
                    // Fallback: show everything if no clear separator found
                    return content;
                  })()}
                </pre>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
